import { Logo } from '@/components/shared/Logo/Logo';
import { SystemBanner } from '@/components/shared/SystemBanner/SystemBanner';
import { SYSTEM_BANNER_HEIGHT, withSystemBannerOffset } from '@/components/shared/SystemBanner/utils/systemBannerHeight';
import { Button } from '@/components/ui/Button/Button';
import { Illustration, type IllustrationType } from '@/components/ui/Illustration/Illustration';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/utils';
import { useTranslation } from 'react-i18next';

export const AuthLayout = ({
  children,
  className,
  style,
  illustration = 'jumping',
  illustrationClassName,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  illustration?: IllustrationType;
  illustrationClassName?: string;
}) => {
  const { t } = useTranslation('components', {
    keyPrefix: 'authLayout',
  });

  const privacyPolicyUrl = import.meta.env.VITE_PRIVACY_POLICY_URL;
  const messagingTermsUrl = import.meta.env.VITE_MESSAGING_TERMS_URL;

  const isDesktop = useMediaQuery('desktop');

  return (
    <div
      className={cn('bg-fill-background-neutral desktop:p-8 flex flex-col', className)}
      style={{ paddingTop: isDesktop ? withSystemBannerOffset(32) : SYSTEM_BANNER_HEIGHT, ...style }}
      {...props}
    >
      <SystemBanner />
      {!isDesktop && (
        <header className='bg-fill-background-neutral desktop:hidden sticky top-[var(--system-banner-height,0px)] z-10 px-5 py-4' role='banner'>
          <Logo variant='image' />
        </header>
      )}
      <div className='max-desktop:min-h-[calc(100vh-76px)] max-desktop:px-10 max-desktop:pt-3 max-desktop:pb-6 desktop:flex-row-reverse max-tablet:justify-start max-tablet:px-5 flex h-full w-full flex-1 flex-col items-center justify-center'>
        <div className='bg-fill-background-light max-desktop:max-h-[calc(100vh-76px)] max-desktop:min-h-56 desktop:sticky desktop:top-[calc(var(--system-banner-height,0px)+2rem)] desktop:right-8 desktop:h-[calc(100vh-64px)] desktop:max-w-[60%] desktop:self-start max-tablet:max-h-[calc(100vh/2-76px)] flex w-full flex-1 items-center justify-center rounded-xl'>
          <Illustration name={illustration} aria-hidden className={cn('tablet:w-[224px] w-[134px]', illustrationClassName)} />
        </div>
        <div className='max-desktop:min-h-[calc(100vh/2-76px)] desktop:w-[40%] flex w-full items-center'>
          {isDesktop && (
            <header
              className='bg-fill-background-neutral max-desktop:hidden fixed top-[calc(var(--system-banner-height,0px)+2rem)] z-10 -mt-8 w-[200px] pt-8'
              role='banner'
            >
              <Logo variant='image' />
            </header>
          )}
          <main className='desktop:my-20 desktop:px-6 my-10 flex w-full justify-center'>{children}</main>
        </div>
      </div>
      <footer className='max-desktop:-mt-10 max-desktop:px-5 -mt-6 flex gap-6'>
        {privacyPolicyUrl && (
          <Button asChild variant='link' className='text-text-default-secondary p-0'>
            <a href={privacyPolicyUrl} target='_blank'>
              {t('privacyPolicy')}
            </a>
          </Button>
        )}
        {messagingTermsUrl && (
          <Button asChild variant='link' className='text-text-default-secondary p-0'>
            <a href={messagingTermsUrl} target='_blank'>
              {t('textMessagingTerms')}
            </a>
          </Button>
        )}
      </footer>
    </div>
  );
};
